import React, { useState } from 'react';
import { Pressable, Text, TextInput, TextInputProps, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { radius, spacing, type, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

interface Props extends TextInputProps {
  label?: string;
  error?: string;
  icon?: keyof typeof Ionicons.glyphMap;
  isPassword?: boolean;
  /** The underlying input, so a form can move focus to it (keyboard Next) or read it. */
  inputRef?: React.Ref<TextInput>;
}

export function TextField({ label, error, icon, isPassword, style, inputRef, ...inputProps }: Props) {
  const { colors } = useAppTheme();
  const [hidden, setHidden] = useState(!!isPassword);
  const [focused, setFocused] = useState(false);

  return (
    <View style={styles.wrap}>
      {label ? <Text style={[type.caption, { color: colors.textSecondary, letterSpacing: 0.6 }]}>{label.toUpperCase()}</Text> : null}
      <View
        style={[
          styles.field,
          {
            backgroundColor: colors.surface,
            borderColor: error ? colors.error : focused ? colors.accent : colors.border,
          },
        ]}>
        {icon && <Ionicons name={icon} size={18} color={colors.textTertiary} />}
        <TextInput
          // Spread first: a caller's onFocus/onBlur used to replace these, so the field never showed
          // its focused border. Theirs still run from inside these.
          {...inputProps}
          ref={inputRef}
          style={[type.body, styles.input, { color: colors.textPrimary }, style]}
          placeholderTextColor={colors.textTertiary}
          secureTextEntry={hidden}
          onFocus={(e) => {
            setFocused(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            inputProps.onBlur?.(e);
          }}
        />
        {isPassword && (
          <Pressable accessibilityRole="button" accessibilityLabel={hidden ? 'Show password' : 'Hide password'} onPress={() => setHidden((v) => !v)} hitSlop={8}>
            <Ionicons name={hidden ? 'eye-off-outline' : 'eye-outline'} size={19} color={colors.textTertiary} />
          </Pressable>
        )}
      </View>
      {error ? (
        <Text accessibilityLiveRegion="polite" style={[type.caption, { color: colors.error }]}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = ScaledSheet.create({
  wrap: { gap: spacing.xs, width: '100%' },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1.5,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    height: 52,
  },
  input: { flex: 1, padding: 0 },
});
